import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Single source of truth for LinkedIn account daily capacity.
 *
 * The DB enforces a hard ceiling per account (25/day premium, 20/day standard)
 * via `account_daily_ceiling()` and the `enforce_campaign_account_capacity`
 * trigger. This hook mirrors that accounting in the UI so users never see
 * negative numbers and never submit a write the DB will reject.
 *
 * Counted statuses match the trigger exactly: draft + paused + active.
 */

export const CAPACITY_COUNTED_STATUSES = ["draft", "paused", "active"] as const;

export function accountDailyCeiling(isPremium?: boolean | null): number {
  return isPremium ? 25 : 20;
}


export interface CapacityCampaign {
  id: string;
  name: string;
  status: string;
  connections: number;
  messages: number;
}

export interface AccountCapacity {
  clientId: string;
  accountName: string;
  capConnections: number;
  capMessages: number;
  /** Total allocated across counted campaigns, EXCLUDING excludeCampaignId. */
  allocatedConnections: number;
  allocatedMessages: number;
  /** Total allocated across ALL counted campaigns (nothing excluded). */
  totalAllocatedConnections: number;
  totalAllocatedMessages: number;
  /** Never negative. */
  availableConnections: number;
  availableMessages: number;
  overAllocatedConnections: number;
  overAllocatedMessages: number;
  isOverAllocated: boolean;
  campaigns: CapacityCampaign[];
  /** The excluded campaign's own current allocation (0 when not applicable). */
  ownConnections: number;
  ownMessages: number;
}

export function capacityQueryKey(clientId?: string | null, excludeCampaignId?: string | null) {
  return ["account-capacity", clientId ?? null, excludeCampaignId ?? null];
}

export async function fetchAccountCapacity(
  clientId: string,
  excludeCampaignId?: string | null
): Promise<AccountCapacity | null> {
  const { data: client, error: clientError } = await supabase
    .from("clients")
    .select("id, linkedin_name, email, is_premium, is_sales_navigator, max_connections_per_day, max_messages_per_day")
    .eq("id", clientId)
    .maybeSingle();

  if (clientError) throw clientError;
  if (!client) return null;

  const ceiling = accountDailyCeiling((client as any).is_premium);
  const capConnections = Math.min((client as any).max_connections_per_day || ceiling, ceiling);
  const capMessages = Math.min((client as any).max_messages_per_day || ceiling, ceiling);

  const { data: rows, error: campaignsError } = await supabase
    .from("campaigns")
    .select("id, name, status, max_connections_daily, max_messages_daily, working_days")
    .eq("client_id", clientId)
    .is("deleted_at", null)
    .in("status", [...CAPACITY_COUNTED_STATUSES]);

  if (campaignsError) throw campaignsError;

  const campaigns: CapacityCampaign[] = (rows || []).map((r: any) => ({
    id: r.id,
    name: r.name || "Untitled campaign",
    status: r.status,
    connections: r.max_connections_daily || 0,
    messages: r.max_messages_daily || 0,
  }));

  const totalAllocatedConnections = campaigns.reduce((s, c) => s + c.connections, 0);
  const totalAllocatedMessages = campaigns.reduce((s, c) => s + c.messages, 0);

  const own = excludeCampaignId ? campaigns.find((c) => c.id === excludeCampaignId) : undefined;
  const ownConnections = own?.connections ?? 0;
  const ownMessages = own?.messages ?? 0;

  const allocatedConnections = totalAllocatedConnections - ownConnections;
  const allocatedMessages = totalAllocatedMessages - ownMessages;

  return {
    clientId,
    accountName: (client as any).linkedin_name || (client as any).email || "this account",
    capConnections,
    capMessages,
    allocatedConnections,
    allocatedMessages,
    totalAllocatedConnections,
    totalAllocatedMessages,
    availableConnections: Math.max(0, capConnections - allocatedConnections),
    availableMessages: Math.max(0, capMessages - allocatedMessages),
    overAllocatedConnections: Math.max(0, totalAllocatedConnections - capConnections),
    overAllocatedMessages: Math.max(0, totalAllocatedMessages - capMessages),
    isOverAllocated:
      totalAllocatedConnections > capConnections || totalAllocatedMessages > capMessages,
    campaigns,
    ownConnections,
    ownMessages,
  };
}

export function useAccountCapacity(
  clientId?: string | null,
  excludeCampaignId?: string | null,
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: capacityQueryKey(clientId, excludeCampaignId),
    queryFn: () => fetchAccountCapacity(clientId as string, excludeCampaignId),
    enabled: !!clientId && (options?.enabled ?? true),
    staleTime: 15_000,
  });

  const capacity = query.data ?? null;

  return {
    ...query,
    capacity,
    /** Max this campaign may claim (available + what it already holds). */
    maxConnectionsForThisCampaign: capacity
      ? Math.max(0, capacity.capConnections - capacity.allocatedConnections)
      : undefined,
    maxMessagesForThisCampaign: capacity
      ? Math.max(0, capacity.capMessages - capacity.allocatedMessages)
      : undefined,
    canFit: (connections: number, messages: number) =>
      !capacity ||
      (capacity.allocatedConnections + connections <= capacity.capConnections &&
        capacity.allocatedMessages + messages <= capacity.capMessages),
    invalidate: () => queryClient.invalidateQueries({ queryKey: ["account-capacity"] }),
  };
}

/** Friendly text for a DB capacity rejection. */
export function isCapacityError(error: unknown): boolean {
  const message = (error as any)?.message || "";
  return typeof message === "string" &&
    message.includes("ACCOUNT_CAPACITY_EXCEEDED");
}

export function formatCapacityError(error: unknown): string {
  const message = String((error as any)?.message || "");
  return message.replace(/^.*ACCOUNT_CAPACITY_EXCEEDED:\s*/, "").trim() ||
    "This LinkedIn account has no daily capacity left. Free up capacity on its other campaigns first.";
}
